import { useEffect, useState } from 'react';
import { api, ApiError, type ProviderAvailability } from '../api';
import { trackEvent } from '../analytics';
import { Logo } from '../components/BrandMarks';
import { useT } from '../i18n';
import {
  EmailIcon,
  FacebookIcon,
  GoogleIcon,
  InstagramIcon,
  WhatsappIcon,
} from '../components/icons';

interface Props {
  navigate: (path: string) => void;
  onSignedIn: () => Promise<void>;
}

type Step =
  | 'providers'
  | 'whatsapp-phone'
  | 'whatsapp-code'
  | 'email-address'
  | 'email-code'
  | 'break-glass';

export function Login({ navigate, onSignedIn }: Props) {
  const t = useT();
  const [available, setAvailable] = useState<ProviderAvailability | null>(null);
  const [step, setStep] = useState<Step>('providers');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [breakGlassCode, setBreakGlassCode] = useState('');
  const [showCode, setShowCode] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  /** Query-string code from an OAuth callback, resolved at render. */
  const [errorCode, setErrorCode] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  useEffect(() => {
    const param = new URLSearchParams(window.location.search).get('error');
    if (param) {
      setErrorCode(param);
      // Drop the query string so a refresh does not re-show the error.
      window.history.replaceState({}, '', '/login');
    }
    api.providers().then(setAvailable).catch(() => setAvailable(null));
  }, []);

  const startOAuth = (provider: 'google' | 'facebook' | 'instagram') => {
    // A full navigation, not fetch — the provider needs to own the next page.
    window.location.href = `/auth/${provider}/start`;
  };

  const sendCode = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.whatsappStart(phone);
      setNotice(t.login.codeSent);
      setStep('whatsapp-code');
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t.login.errorSendCode);
    } finally {
      setBusy(false);
    }
  };

  const verifyCode = async () => {
    setBusy(true);
    setError(null);
    try {
      const res = await api.whatsappVerify(phone, code);
      trackEvent('Auth', 'login_success', 'whatsapp');
      await onSignedIn();
      navigate(res.role === 'staff' ? '/admin' : '/perfil');
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t.login.errorVerifyCode);
    } finally {
      setBusy(false);
    }
  };

  const sendEmail = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.emailStart(email);
      setNotice(t.login.emailSent);
      setStep('email-code');
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t.login.errorSendEmail);
    } finally {
      setBusy(false);
    }
  };

  const verifyEmailCode = async () => {
    setBusy(true);
    setError(null);
    try {
      const res = await api.emailVerify(email, code);
      trackEvent('Auth', 'login_success', 'email');
      await onSignedIn();
      navigate(res.role === 'staff' ? '/admin' : '/perfil');
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t.login.errorVerifyCode);
    } finally {
      setBusy(false);
    }
  };

  const signInWithBreakGlass = async () => {
    setBusy(true);
    setError(null);
    try {
      const res = await api.breakGlass(email, breakGlassCode);
      trackEvent('Auth', 'login_success', 'break_glass');
      await onSignedIn();
      navigate(res.role === 'staff' ? '/admin' : '/perfil');
    } catch (err) {
      setBreakGlassCode('');
      setError(err instanceof ApiError ? err.message : t.login.errorVerifyCode);
    } finally {
      setBusy(false);
    }
  };

  const shownError =
    error ??
    (errorCode
      ? (t.loginErrors[errorCode as keyof typeof t.loginErrors] ?? t.login.errorSignIn)
      : null);

  const unavailable = (key: keyof ProviderAvailability) => available !== null && !available[key];

  // Every button greyed out with no explanation is a dead end — say why.
  // Break-glass does not count: it is staff-only, so a customer facing five
  // disabled buttons still needs to be told nothing is configured for them.
  const noneConfigured =
    available !== null &&
    !(['google', 'facebook', 'instagram', 'whatsapp', 'email'] as const).some(
      (key) => available[key],
    );

  return (
    <div className="narrow screen">
      <div className="login-head">
        <Logo title="Caffeina" />
        <div className="eyebrow eyebrow--tight" style={{ marginBottom: 10 }}>
          {t.login.eyebrow}
        </div>
        <h1 className="login-title">{t.login.title}</h1>
      </div>

      {shownError && (
        <div className="notice notice--error" style={{ marginBottom: 16 }} role="alert">
          {shownError}
        </div>
      )}

      {noneConfigured && (
        <div className="notice" style={{ marginBottom: 16 }} role="status">
          {t.login.noneConfigured}
        </div>
      )}

      {step === 'providers' && (
        <>
          <div className="provider-list">
            <button
              type="button"
              className="provider-btn"
              onClick={() => {
                setError(null);
                setStep('email-address');
              }}
              disabled={unavailable('email')}
            >
              <EmailIcon />
              {t.login.continueWith('email')}
              {unavailable('email') && <span className="provider-btn__soon">{t.login.soon}</span>}
            </button>
            <button
              type="button"
              className="provider-btn"
              onClick={() => startOAuth('google')}
              disabled={unavailable('google')}
            >
              <GoogleIcon />
              {t.login.continueWith('Google')}
              {unavailable('google') && <span className="provider-btn__soon">{t.login.soon}</span>}
            </button>
            <button
              type="button"
              className="provider-btn"
              onClick={() => startOAuth('instagram')}
              disabled={unavailable('instagram')}
            >
              <InstagramIcon />
              {t.login.continueWith('Instagram')}
              {unavailable('instagram') && <span className="provider-btn__soon">{t.login.soon}</span>}
            </button>
            <button
              type="button"
              className="provider-btn"
              onClick={() => startOAuth('facebook')}
              disabled={unavailable('facebook')}
            >
              <FacebookIcon />
              {t.login.continueWith('Facebook')}
              {unavailable('facebook') && <span className="provider-btn__soon">{t.login.soon}</span>}
            </button>
            <button
              type="button"
              className="provider-btn"
              onClick={() => {
                setError(null);
                setStep('whatsapp-phone');
              }}
              disabled={unavailable('whatsapp')}
            >
              <WhatsappIcon />
              {t.login.continueWith('WhatsApp')}
              {unavailable('whatsapp') && <span className="provider-btn__soon">{t.login.soon}</span>}
            </button>
          </div>

          <p className="login-footnote">
            {t.login.footnoteLine1}
            <br />
            {t.login.footnoteLine2}
          </p>

          {available?.breakGlass && (
            <button
              type="button"
              className="btn--quiet"
              style={{ marginTop: 4, textTransform: 'none', fontSize: 12, letterSpacing: 0 }}
              onClick={() => {
                setError(null);
                setStep('break-glass');
              }}
            >
              {t.login.breakGlassLink}
            </button>
          )}
        </>
      )}

      {step === 'break-glass' && (
        <div className="provider-list">
          <div className="notice" role="status">
            {t.login.breakGlassIntro}
          </div>
          <label className="eyebrow eyebrow--tight" htmlFor="bg-email">
            {t.login.breakGlassEmail}
          </label>
          <input
            id="bg-email"
            className="text-input"
            type="email"
            inputMode="email"
            autoComplete="email"
            placeholder={t.login.emailPlaceholder}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
          <label className="eyebrow eyebrow--tight" htmlFor="bg-code">
            {t.login.breakGlassCode}
          </label>
          <input
            id="bg-code"
            className="text-input"
            // Masked by default for the café floor, but revealable: this is a
            // long code typed under pressure, and silently mistyping it is the
            // one failure the whole feature exists to avoid.
            type={showCode ? 'text' : 'password'}
            // Never offer to save it: this is a shared café tablet, and the
            // browser remembering it would defeat the point of a secret.
            autoComplete="off"
            autoCapitalize="none"
            autoCorrect="off"
            spellCheck={false}
            placeholder="xxxx-xxxx-xxxx-xxxx-xxxx-xxxx"
            value={breakGlassCode}
            onChange={(e) => setBreakGlassCode(e.target.value)}
          />
          <button
            type="button"
            className="btn--quiet"
            style={{ textTransform: 'none', fontSize: 13, letterSpacing: 0 }}
            onClick={() => setShowCode((v) => !v)}
          >
            {showCode ? t.login.hideCode : t.login.showCode}
          </button>
          <p className="login-footnote" style={{ marginTop: 0 }}>
            {t.login.codeFormatHint}
          </p>
          <button
            type="button"
            className="btn btn--solid"
            style={{ padding: 15, fontSize: 15 }}
            onClick={signInWithBreakGlass}
            disabled={
              busy || !email.trim() || breakGlassCode.replace(/[^a-zA-Z0-9]/g, '').length < 8
            }
          >
            {busy ? t.login.verifying : t.login.enter}
          </button>
          <button
            type="button"
            className="btn--quiet"
            onClick={() => {
              setBreakGlassCode('');
              setShowCode(false);
              setError(null);
              setStep('providers');
            }}
          >
            {t.login.otherMethod}
          </button>
        </div>
      )}

      {step === 'whatsapp-phone' && (
        <div className="provider-list">
          <label className="eyebrow eyebrow--tight" htmlFor="phone">
            {t.login.phoneLabel}
          </label>
          <input
            id="phone"
            className="text-input"
            type="tel"
            inputMode="tel"
            autoComplete="tel"
            placeholder="+54 9 11 5555 5555"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
          />
          <button
            type="button"
            className="btn btn--solid"
            style={{ padding: 15, fontSize: 15 }}
            onClick={sendCode}
            disabled={busy || phone.replace(/\D/g, '').length < 8}
          >
            {busy ? t.login.sending : t.login.sendCode}
          </button>
          <button type="button" className="btn--quiet" onClick={() => setStep('providers')}>
            {t.login.otherMethod}
          </button>
        </div>
      )}

      {step === 'whatsapp-code' && (
        <div className="provider-list">
          {notice && <div className="notice">{notice}</div>}
          <label className="eyebrow eyebrow--tight" htmlFor="code">
            {t.login.codeLabel}
          </label>
          <input
            id="code"
            className="text-input otp-input"
            type="text"
            inputMode="numeric"
            autoComplete="one-time-code"
            maxLength={6}
            placeholder="000000"
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
          />
          <button
            type="button"
            className="btn btn--solid"
            style={{ padding: 15, fontSize: 15 }}
            onClick={verifyCode}
            disabled={busy || code.length !== 6}
          >
            {busy ? t.login.verifying : t.login.enter}
          </button>
          <button
            type="button"
            className="btn--quiet"
            onClick={() => {
              setCode('');
              setNotice(null);
              setStep('whatsapp-phone');
            }}
          >
            {t.login.changePhone}
          </button>
        </div>
      )}

      {step === 'email-address' && (
        <div className="provider-list">
          <label className="eyebrow eyebrow--tight" htmlFor="email">
            {t.login.emailLabel}
          </label>
          <input
            id="email"
            className="text-input"
            type="email"
            inputMode="email"
            autoComplete="email"
            placeholder="vos@ejemplo.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
          <button
            type="button"
            className="btn btn--solid"
            style={{ padding: 15, fontSize: 15 }}
            onClick={sendEmail}
            disabled={busy || !/^[^\s@]+@[^\s@.]+\.[^\s@]+$/.test(email.trim())}
          >
            {busy ? t.login.sending : t.login.sendLink}
          </button>
          <button type="button" className="btn--quiet" onClick={() => setStep('providers')}>
            {t.login.otherMethod}
          </button>
        </div>
      )}

      {step === 'email-code' && (
        <div className="provider-list">
          {notice && <div className="notice">{notice}</div>}
          <p className="login-footnote" style={{ marginTop: 0 }}>
            {t.login.emailCodeHint}
          </p>
          <label className="eyebrow eyebrow--tight" htmlFor="email-code">
            {t.login.codeLabel}
          </label>
          <input
            id="email-code"
            className="text-input otp-input"
            type="text"
            inputMode="numeric"
            autoComplete="one-time-code"
            maxLength={6}
            placeholder="000000"
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
          />
          <button
            type="button"
            className="btn btn--solid"
            style={{ padding: 15, fontSize: 15 }}
            onClick={verifyEmailCode}
            disabled={busy || code.length !== 6}
          >
            {busy ? t.login.verifying : t.login.enter}
          </button>
          <button
            type="button"
            className="btn--quiet"
            onClick={() => {
              setCode('');
              setNotice(null);
              setStep('email-address');
            }}
          >
            {t.login.changeEmail}
          </button>
        </div>
      )}

      <button
        type="button"
        className="btn--quiet"
        style={{ marginTop: 28, textTransform: 'none', fontSize: 12, letterSpacing: 0 }}
        onClick={() => navigate('/')}
      >
        {t.login.backToSite}
      </button>
    </div>
  );
}
